import { FC, useEffect, useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import Transition from 'react-transition-group/Transition';
import { CycleI, PackName } from '~src/interfaces/cycle.interface';
import { SelectionValue } from '~src/interfaces/question.interface';
import { SelectionType, UpdateSelfSelection } from '~src/services/api/pulseCheck';
import { defaultPulseCheckApi } from '~src/services/ApiController';

import logger from '~src/services/logger/LlsLogger';
import { translateContent } from '~src/services/translationServices';
import { Mobile } from '~src/styles/containers.sc';
import CardNavigation from '~src/modules/questions/components/CardNavigation';
import LoadingCard from '~src/modules/questions/components/cards/LoadingCard';
import SelectionCard from '~src/modules/questions/components/cards/SelectionCard';
import MobileTongue from '~src/modules/questions/components/MobileTongue';
import PopoverInfo from '~src/modules/questions/components/PopoverInfo';
import QuestionsInfo from '~src/modules/questions/components/QuestionsInfo';
import { NavigationContainer } from '~src/modules/questions/components/shared.sc';

import { QuestionsGrid, QuestionsWrapper } from './questions.sc';
import { animationSpeed, CardAnimateur } from './stream.sc';

interface OwnProps {
  finishQS: (pack: PackName) => void;
  cycle: CycleI;
  precedent?: string;
}

const CustomSelection: FC<OwnProps> = (props: OwnProps) => {
  const [customSelection, setCustomSelection] = useState<SelectionType | undefined>(undefined);
  const [isIn, setWantCardShown] = useState<boolean>(false);
  const [isLoadingStream, setIsLoadingStream] = useState<boolean>(true);
  const [isSaveLoading, setIsSaveLoading] = useState<boolean>(false);

  useEffect(() => {
    setIsLoadingStream(true);
    const controller = new AbortController();

    defaultPulseCheckApi
      .withSignal(controller.signal)
      .getCustomSelection(props.cycle.id, props.precedent)
      .then((response) => {
        setCustomSelection(response.data[0]);
        setWantCardShown(true);
        setIsLoadingStream(false);
      })
      .catch((e) => {
        throw e;
      });

    return () => {
      controller.abort();
    };
  }, []);

  const sendAnswer = (value: SelectionValue) => {
    if (!customSelection) {
      logger.warn('trying to sendAnswer, but no current customSelection');
      return;
    }
    const payload: UpdateSelfSelection = {
      about_id: customSelection.about?.id,
      ask_ids: value.selectedPeople.map((val) => val.id),
      cycle_id: props.cycle.id,
    };
    setIsSaveLoading(true);
    defaultPulseCheckApi
      .postCustomSelection(payload)
      .then(() => {
        setIsSaveLoading(false);
        customSelection.asked = value.selectedPeople;
        nextCard();
      })
      .catch((error: Error) => {
        logger.info('Error updating CustomSelection', {
          error,
          info: {
            componentStack: 'CustomSelection.tsx',
          },
        });
        throw error;
      })
      .finally(() => {
        setIsSaveLoading(false);
      });
  };

  /* In the case of Self selection, there is only one Card so every movement will finish the Stream*/
  const nextCard = () => {
    setWantCardShown(false);
    setTimeout(() => {
      props.finishQS(PackName.CustomSelector);
    }, animationSpeed);
  };
  const swipeableHandlers = useSwipeable({
    onSwipedLeft: () => nextCard(),
  });
  return (
    <>
      <QuestionsWrapper>
        <QuestionsGrid>
          {!customSelection || isLoadingStream ? (
            <LoadingCard />
          ) : (
            <>
              <Mobile>
                <PopoverInfo selection={PackName.CustomSelector} />
              </Mobile>
              <Transition in={isIn} timeout={animationSpeed}>
                {(status) => (
                  <CardAnimateur $direction={'left'} className={status}>
                    <div {...swipeableHandlers}>
                      <SelectionCard
                        selectionType={PackName.CustomSelector}
                        currentCard={{ ...customSelection, asked: customSelection.customSelection }}
                        index={0}
                        onSubmit={sendAnswer}
                        saveLoading={isSaveLoading}
                      />
                    </div>
                  </CardAnimateur>
                )}
              </Transition>
              <NavigationContainer>
                <CardNavigation next={nextCard} max={1} value={1} />
              </NavigationContainer>
              <QuestionsInfo
                count={1}
                dueDate={props.cycle.dueDate}
                selection={PackName.CustomSelector}
                surveyName={translateContent(props.cycle.survey.name)}
              />
            </>
          )}
        </QuestionsGrid>
      </QuestionsWrapper>
      {customSelection && !isLoadingStream && (
        <MobileTongue
          name={translateContent(props.cycle.survey.name)}
          surveyLength={1}
          dueDate={props.cycle.dueDate}
        />
      )}
    </>
  );
};

export default CustomSelection;
